import { defineComponent as _defineComponent } from 'vue'
import { computed, Ref, ref } from 'vue'
import VsForm from '@/components/vision/VsForm.vue'
import TagsDisplay from '@/components/common/tags/TagsDisplay.vue'
import VsButton from '@/components/vision/VsButton.vue'
import NewTag from '@/models/new_tag'
import NewAutocomplete from '@/components/vision/next/inputs/NewAutocomplete.vue'
import { SearchOptions } from '@/components/vision/next/inputs/UseNewAutocomplete'
import { useSearchTagCategories, useSearchTagValues } from '@/api/tags'
import { PickerItemType } from '@/components/vision/next/picker-list/models'
import { watchOnce } from '@vueuse/core'
import { useCurrentOrg } from '@/common/useCurrentOrg'


export default /*#__PURE__*/_defineComponent({
  __name: 'TagsEditor',
  props: {
    initialTags: null,
    hubId: null
  },
  emits: ['error', 'update'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as {
  initialTags: NewTag[]
  hubId?: string
}

/*
 * This component is used to display and edit tags. It accepts an INITIAL tags value, and emits an UPDATE event when the tags are changed.
 * The tags are displayed as chips, and can be removed by clicking on them.
 * Note: initialTags will not be treated as a reactive prop, i.e., changes to initialTags will not be reflected in this component unless the component is re-created.
 */


const currentTags = ref<NewTag[]>([])
const tagCategoryAutocomplete = ref()
const tagValueAutocomplete = ref()
const form = ref()
const { currentOrg } = useCurrentOrg()
watchOnce(
  () => props.initialTags,
  () => {
    currentTags.value = [...props.initialTags]
  },
  { immediate: true }
)
const hasChanges = computed(() => {
  if (currentTags.value.length !== props.initialTags.length) {
    return true
  }
  for (let i = 0; i < currentTags.value.length; i++) {
    let found = false
    for (let j = 0; j < props.initialTags.length; j++) {
      if (
        ((!currentTags.value[i].category && !props.initialTags[j].category) ||
          currentTags.value[i].category?.toLowerCase() ===
            props.initialTags[j].category?.toLowerCase()) &&
        currentTags.value[i].value.toLowerCase() ===
          props.initialTags[j].value.toLowerCase() &&
        currentTags.value[i].isEditable === props.initialTags[j].isEditable
      ) {
        found = true
        break
      }
    }
    if (!found) {
      return true
    }
  }
  return false
})
const newCategory = ref('')
const newValue = ref('')
const updateNewCategory = (newVal?: string | null) => {
  newCategory.value = newVal ?? ''
}
const updateNewValue = (newVal?: string | null) => {
  newValue.value = newVal ?? ''
}


const handleDeleteTag = (arg: NewTag) => {
  if (!arg.isEditable) return
  currentTags.value = currentTags.value.filter(
    (tag) =>
      !(
        tag.category === arg.category &&
        tag.value === arg.value &&
        tag.isEditable
      )
  )
  emits('update', currentTags.value)
}

const handleAddTag = () => {
  if (!form.value.isValid()) {
    return
  }
  if (newValue.value) {
    if (
      currentTags.value.some(
        (tag) =>
          ((!tag.category && !newCategory.value) ||
            tag.category?.toLowerCase() ===
              newCategory.value.toLowerCase().trim()) &&
          tag.value.toLowerCase() === newValue.value.toLowerCase().trim() &&
          tag.isEditable
      )
    ) {
      emits('error', 'Tag already exists')
      return
    }
    currentTags.value.push(
      new NewTag(
        newCategory.value ? newCategory.value.trim() : null,
        newValue.value.trim(),
        true
      )
    )
    emits('update', currentTags.value)
    tagCategoryAutocomplete.value.clear()
    tagValueAutocomplete.value.clear()
  }
}

const validateTagContent = (toValidate: string, propertyName: string) => {
  const invalidChars = [':', ',']
  for (let i = 0; i < invalidChars.length; i++) {
    if (toValidate.includes(invalidChars[i])) {
      return `${propertyName} cannot contain '${invalidChars[i]}'`
    }
  }
}

const searchTagCategories = (options: Ref<SearchOptions>) => {
  const payload = computed(() => {
    if (props.hubId) {
      return {
        searchTerm: options.value.searchText,
        hubId: props.hubId,
      }
    }
    return {
      searchTerm: options.value.searchText,
      orgId: currentOrg.value?.id,
    }
  })

  const {
    data: rawItems,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  } = useSearchTagCategories(payload)

  const items = computed<Readonly<PickerItemType[]>>(() => {
    return (
      rawItems.value?.pages
        .flatMap((p) => p.data)
        .map((l) => ({
          id: l,
          title: l,
          selectable: true,
        })) || []
    )
  })

  return {
    items,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  }
}

const valueResolverForTagCategory = async (id: string | null) => {
  return { id, title: id }
}

const searchTagValues = (options: Ref<SearchOptions>) => {
  const payload = computed(() => {
    if (props.hubId) {
      return {
        searchTerm: options.value.searchText,
        category: newCategory.value,
        hubId: props.hubId,
      }
    }
    return {
      searchTerm: options.value.searchText,
      category: newCategory.value,
      orgId: currentOrg.value?.id,
    }
  })

  const {
    data: rawItems,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  } = useSearchTagValues(payload)

  const items = computed<Readonly<PickerItemType[]>>(() => {
    return (
      rawItems.value?.pages
        .flatMap((p) => p.data)
        .map((l) => ({
          id: l,
          title: l,
          selectable: true,
        })) || []
    )
  })

  return {
    items,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetched,
    isInitialLoading,
  }
}

const valueResolverForTagValue = async (id: string | null) => {
  return { id, title: id }
}

expose({ hasChanges, currentTags })

return { __sfc: true,props, currentTags, tagCategoryAutocomplete, tagValueAutocomplete, form, currentOrg, hasChanges, newCategory, newValue, updateNewCategory, updateNewValue, emits, handleDeleteTag, handleAddTag, validateTagContent, searchTagCategories, valueResolverForTagCategory, searchTagValues, valueResolverForTagValue, VsForm, TagsDisplay, VsButton, NewAutocomplete }
}

})